import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  TrendingUp,
  Target,
  Calendar,
  Download,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const Analytics: React.FC = () => {
  const data = [
    {
      name: 'Jan',
      applicants: 45,
      hires: 12,
      quality: 78,
    },
    {
      name: 'Feb',
      applicants: 52,
      hires: 15,
      quality: 82,
    },
    {
      name: 'Mar',
      applicants: 48,
      hires: 10,
      quality: 75,
    },
    {
      name: 'Apr',
      applicants: 61,
      hires: 18,
      quality: 88,
    },
    {
      name: 'May',
      applicants: 55,
      hires: 14,
      quality: 80,
    },
    {
      name: 'Jun',
      applicants: 67,
      hires: 22,
      quality: 85,
    },
  ];

  const sourceData = [
    { name: 'LinkedIn', value: 45, color: '#0052CC' },
    { name: 'Referral', value: 25, color: '#36B37E' },
    { name: 'Indeed', value: 15, color: '#FFAB00' },
    { name: 'Direct', value: 10, color: '#FF5630' },
    { name: 'Others', value: 5, color: '#6554C0' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-main">Recruitment Analytics</h1>
          <p className="text-text-subtle">
            Track your hiring performance and candidate quality metrics.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="flex items-center gap-2">
            <Filter size={16} /> Filter Data
          </Button>
          <Button className="flex items-center gap-2">
            <Download size={16} /> Export Report
          </Button>
        </div>
      </div>

      {/* High-Level Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          {
            label: 'Time to Hire',
            value: '18 Days',
            trend: '+2.4%',
            up: false,
            icon: Calendar,
            color: 'text-primary',
          },
          {
            label: 'Cost per Hire',
            value: '$2,450',
            trend: '-12.5%',
            up: true,
            icon: Target,
            color: 'text-success',
          },
          {
            label: 'Offer Acceptance',
            value: '84%',
            trend: '+4.2%',
            up: true,
            icon: TrendingUp,
            color: 'text-info',
          },
          {
            label: 'Source Efficiency',
            value: 'LinkedIn',
            trend: 'Top Performer',
            up: true,
            icon: Briefcase,
            color: 'text-violet-600 dark:text-violet-400',
          },
        ].map((stat, i) => (
          <div key={i} className="bg-card p-6 rounded-xl border border-border-subtle shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div className={`p-2 rounded-lg bg-bg-main ${stat.color}`}>
                <stat.icon size={20} />
              </div>
              <div
                className={`flex items-center gap-1 text-sm font-bold ${stat.up ? 'text-success' : 'text-destructive'}`}
              >
                {stat.up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {stat.trend}
              </div>
            </div>
            <div className="text-sm font-semibold text-text-subtle uppercase tracking-wider">
              {stat.label}
            </div>
            <div className="text-3xl font-bold text-text-main mt-1">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Applicant Volume Area Chart */}
        <div className="bg-card border border-border-subtle p-8 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-lg font-bold text-text-main">Applicant Volume</h3>
            <Select defaultValue="6m">
              <SelectTrigger className="w-34 h-8 text-sm font-bold text-text-subtle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6m">Last 6 Months</SelectItem>
                <SelectItem value="1y">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="h-75">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorApplicants" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0052CC" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#0052CC" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F4F5F7" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#5E6C84', fontSize: 12 }}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#5E6C84', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #DFE1E6',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="applicants"
                  stroke="#0052CC"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorApplicants)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Hiring Success Bar Chart */}
        <div className="bg-card border border-border-subtle p-8 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-lg font-bold text-text-main">Hiring Success Rate</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span className="text-sm text-text-subtle">Applicants</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-success" />
                <span className="text-sm text-text-subtle">Hires</span>
              </div>
            </div>
          </div>
          <div className="h-75">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F4F5F7" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#5E6C84', fontSize: 12 }}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#5E6C84', fontSize: 12 }} />
                <Tooltip
                  cursor={{ fill: '#F4F5F7' }}
                  contentStyle={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #DFE1E6',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }}
                />
                <Bar dataKey="applicants" fill="#0052CC" radius={[4, 4, 0, 0]} barSize={20} />
                <Bar dataKey="hires" fill="#36B37E" radius={[4, 4, 0, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Candidate Quality Line Chart */}
        <div className="bg-card border border-border-subtle p-8 rounded-xl shadow-sm">
          <h3 className="text-lg font-bold text-text-main mb-8">Average Candidate Quality</h3>
          <div className="h-75">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F4F5F7" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#5E6C84', fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#5E6C84', fontSize: 12 }}
                  domain={[0, 100]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #DFE1E6',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="quality"
                  stroke="#6554C0"
                  strokeWidth={3}
                  dot={{
                    r: 4,
                    fill: '#6554C0',
                    strokeWidth: 2,
                    stroke: '#fff',
                  }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Source Distribution Pie Chart */}
        <div className="bg-card border border-border-subtle p-8 rounded-xl shadow-sm">
          <h3 className="text-lg font-bold text-text-main mb-8">Candidate Source Distribution</h3>
          <div className="h-75 flex items-center">
            <div className="flex-1 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #DFE1E6',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col gap-4 pl-8">
              {sourceData.map((entry, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-text-main">{entry.name}</span>
                    <span className="text-xs text-text-subtle font-medium">{entry.value}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
